import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
} from '@angular/core';
import { translate } from '@ngneat/transloco';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ChartData } from '../../models/chart-data.class';
import { WidgetData } from '../../models/widget-data.interface';
import { Widget } from '../../models/widget.interface';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { DashboardService } from '../../services/dashboard/dashboard.service';
import { RootScopeService } from '../../services/rootscope/rootscope.service';
import { UtilsService } from '../../services/utils/utils.service';

@Component({
    selector: 'app-chart-data',
    templateUrl: './chart-data.component.html',
    styleUrls: ['./chart-data.component.scss'],
    standalone: false,
})
export class ChartDataComponent implements OnChanges, AfterViewInit, OnInit, OnDestroy {
    @Input() widget: Widget;
    @Input() width: number;
    @Input() height: number;
    @Input() isFromEnCollab: boolean;
    @Input() chartDetails: BehaviorSubject<WidgetData>;
    @Input() index: number;
    @Input() legendToggle: boolean;

    @Output() emitChartURL: EventEmitter<string> = new EventEmitter();

    id = 'chart-' + this._libUtils.guid(8);
    loading = true;
    isDataPresent: boolean;
    data: WidgetData;
    chartData: ChartData;
    unsubscribe = new Subject<void>();

    constructor(
        private _commonUtils: CommonUtilsService,
        private _libUtils: UtilsService,
        private _dashboard: DashboardService,
        private R: RootScopeService,
        private renderer: Renderer2
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.width || changes.height) {
            this.adjustParentHeight();
        }
        if (changes.legendToggle && this.chartData) {
            this.chartData.legendToggle = this.legendToggle;
            this.chartData.update(this.data);
        }
    }

    startComponent = async (refresh?: boolean, details?, lastAction?: 'refresh') => {
        if (details?.deleted || this.widget?.deleted) {
            return;
        }
        if (this.widget) {
            this.widget.reportUrl = undefined;
            this.widget.lastRefresh = undefined;
            this.loading = true;
        }
        let response;
        if (this.isFromEnCollab) {
            response = details;
        } else {
            if (!refresh) {
                const organizationId = this._commonUtils.getFromStorage('currentOrganizationId');
                const requestStatus = await this._dashboard.getWidgetStatus(`${organizationId}/${this.widget.id}`);
                if (requestStatus === undefined) {
                    return this.startComponent(true, details);
                } else if (!requestStatus) {
                    return;
                }
            }
            response = await this.getWidgetData(refresh);
            if (!response && refresh) {
                return this.startComponent(undefined, details, refresh ? 'refresh' : undefined);
            }
        }
        if (response?.chartType === 'PIE_CHART') {
            if (response['segments'] && response['segments'].length > 0) {
                const segments: { name: string; value: number }[] = response['segments'];
                response.dataSet = [
                    {
                        coordinatesList: [],
                        name: response.name,
                    },
                ];
                const list = response.dataSet[0].coordinatesList;
                segments.forEach(segment => {
                    list.push({
                        x: segment.name,
                        y: segment.value,
                    });
                });
            }
            const minWidth = response?.dataSet?.reduce((width: number, set) => {
                width += (set?.coordinatesList?.length || 0) * 9;
                return width;
            }, 500);
            response.minWidth = minWidth;
            response.minHeight = minWidth;
        } else if (response?.chartType === 'BAR_CHART') {
            response.minWidth = response?.dataSet?.reduce((width: number, set) => {
                width += (set?.coordinatesList?.length || 0) * 40 + 20 * 2;
                return width;
            }, 0);
        } else if (response?.chartType === 'LINE_CHART') {
            response.minWidth = response?.dataSet?.reduce((width: number) => {
                width += 30;
                return width;
            }, 0);
        }
        response && (response.dataSet = response.dataSet || []);
        let found = false;
        if (response) {
            response.dataSet.forEach(set => {
                found = found || set?.coordinatesList?.length > 0;
            });
            this.isDataPresent = found;
        }
        this.data = response;
        this.loading = false;
        const dataSets = this.chartData?.chartConfiguration?.data.datasets || [];
        let previousData = dataSets.length > 0;
        dataSets.forEach(set => (previousData = previousData && set.data.length > 0));
        if (this.isDataPresent || previousData) {
            this.isDataPresent = found;
            setTimeout(() => {
                if (!this.chartData) {
                    this.constructCanvas();
                } else {
                    this.chartData.update(this.data);
                }
            }, 10);
        }
        if (refresh || lastAction === 'refresh') {
            this._libUtils.alertSuccess(translate('Dashboard refreshed successfully'));
        }
    };

    getWidgetData = (refresh?: boolean): Promise<WidgetData> => {
        return new Promise(resolve => {
            const data = {
                widgetId: this.widget.id,
                refresh: refresh || false,
                userName: this._commonUtils.getFromStorage('user').id ? this._commonUtils.getFromStorage('user').id : '',
                showLoader: false,
            };
            this.loading = true;
            this.widget.errorMessage = undefined;
            this._dashboard.getWidgetData(data, {
                successCallback: res => {
                    this.loading = false;
                    const chartData = res.response?.widgetResponse.length > 0 && res.response?.widgetResponse[0].charts[0];
                    resolve(chartData);
                    this.R.widgetChartData[this.widget.id] = chartData;
                    const lastRefresh = res?.response?.widgetResponse?.[0]?.lastRefresh;
                    this.widget.lastRefresh = lastRefresh;
                    if (lastRefresh?.toLowerCase()?.indexOf('last') === -1) {
                        this.widget.lastRefresh = this._commonUtils.transformDateToLocale(
                            lastRefresh,
                            'DD-MM-YYYY HH:MM:SS AM',
                            'DD-MM-YYYY HH:MM:SS AM',
                            false
                        );
                    }
                    this.widget.reportUrl = res?.response?.widget?.reportUrl && encodeURIComponent(res?.response?.widget?.reportUrl);
                },
                failureCallback: async res => {
                    this.widget.errorMessage = res?.msg;
                    if (res?.response?.widget?.requestState === 'TERMINATED') {
                        this.loading = false;
                        return this._libUtils.alertError(res.msg);
                    } else if (res?.response?.widget?.requestState === 'IN_PROGRESS') {
                        await this._libUtils.waitForCertainTime(1000);
                        this.startComponent(undefined, undefined, refresh ? 'refresh' : undefined);
                        return;
                    }
                    if (!refresh && res?.msg) {
                        this._libUtils.alertError(res.msg);
                    }
                    !refresh && (this.loading = false);
                    resolve(false as any);
                },
            });
        });
    };

    private constructCanvas = () => {
        const parentElement = document.querySelector('.' + this.id) as HTMLDivElement;
        this.chartData = new ChartData(parentElement, this.data, this.isFromEnCollab, this.legendToggle);
        this.chartData.chartImageURL.pipe(takeUntil(this.unsubscribe))?.subscribe(url => {
            this.emitChartURL.emit(url);
        });
    };

    private adjustParentHeight = () => {
        const containerElement = document.querySelector('.' + this.id) as HTMLDivElement;
        const width = containerElement?.offsetWidth;
        const height = containerElement?.offsetHeight;
        const canvasElement = document.querySelector('#' + this.id) as HTMLCanvasElement;
        if (width > 0 && height > 0) {
            const parent = canvasElement.parentNode as HTMLDivElement;
            if (parent) {
                this.renderer.setStyle(parent, 'width', Math.max(this.chartData.minWidth, width) + 'px');
                this.renderer.setStyle(parent, 'height', Math.max(this.chartData.minHeight, height) + 'px');
            }
        }
    };

    private updateData = () => {
        this.startComponent(true);
    };

    ngAfterViewInit(): void {
        if (!this.isFromEnCollab) {
            this.startComponent();
            if (this.widget) {
                this.widget.updateData = this.updateData;
            }
        }
    }

    ngOnInit() {
        this.chartDetails?.pipe(takeUntil(this.unsubscribe))?.subscribe(data => {
            if (data) {
                const refresh = this.chartData !== undefined ? true : undefined;
                this.chartData = undefined;
                this.startComponent(refresh, data);
            }
        });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
